import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DocumentPreview = ({ document, onClose, onSign, onAddComment }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');

  const totalPages = 12; // Mock total pages

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 25, 50));
  };

  const handleAddComment = () => {
    if (newComment?.trim()) {
      onAddComment(document?.id, newComment, currentPage);
      setNewComment('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon 
              name={document?.type === 'BPD' ? 'FileText' : 'FileCode'} 
              size={24} 
              className="text-primary"
            />
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {document?.title}
              </h2>
              <p className="text-sm text-muted-foreground">
                Version {document?.version} • {totalPages} pages
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              iconName="MessageSquare"
              onClick={() => setShowComments(!showComments)}
            >
              Comments ({document?.comments?.length || 0})
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="Download"
            >
              Download
            </Button>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
            />
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Document Viewer */}
          <div className="flex-1 flex flex-col">
            {/* Toolbar */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/30">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ChevronLeft"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  />
                  <span className="text-sm text-foreground min-w-[80px] text-center">
                    {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ChevronRight"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  />
                </div>
                <div className="w-px h-6 bg-border" />
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ZoomOut"
                    onClick={handleZoomOut}
                    disabled={zoomLevel <= 50}
                  />
                  <span className="text-sm text-foreground min-w-[60px] text-center">
                    {zoomLevel}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="ZoomIn"
                    onClick={handleZoomIn}
                    disabled={zoomLevel >= 200}
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="RotateCcw"
                >
                  Reset View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Maximize2"
                >
                  Fullscreen
                </Button>
              </div>
            </div>

            {/* Document Content */}
            <div className="flex-1 overflow-auto bg-muted/20 p-8">
              <div 
                className="bg-white shadow-elevation-2 mx-auto"
                style={{ 
                  width: `${(8.5 * 96 * zoomLevel) / 100}px`,
                  minHeight: `${(11 * 96 * zoomLevel) / 100}px`
                }}
              >
                <div className="p-8 text-gray-800">
                  <div className="text-center mb-8">
                    <h1 className="text-2xl font-bold mb-2">
                      Business Process Design Document (BPD²)
                    </h1>
                    <h2 className="text-xl text-gray-600 mb-4">
                      Order-to-Cash Process Implementation
                    </h2>
                    <div className="text-sm text-gray-500">
                      <p>Document Version: {document?.version}</p>
                      <p>Created: {new Date(document.createdAt)?.toLocaleDateString()}</p>
                      <p>Page {currentPage} of {totalPages}</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <section>
                      <h3 className="text-lg font-semibold mb-3 text-gray-800">
                        1. Executive Summary
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        This Business Process Design Document outlines the implementation approach for the Order-to-Cash process using SAP Best Practices. The solution encompasses customer inquiry management, quotation processing, sales order creation, delivery execution, and invoice processing with integrated credit management capabilities.
                      </p>
                    </section>

                    <section>
                      <h3 className="text-lg font-semibold mb-3 text-gray-800">
                        2. Process Scope
                      </h3>
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <ul className="space-y-2 text-gray-700">
                          <li>• Customer Master Data Management</li>
                          <li>• Sales Inquiry and Quotation Processing</li>
                          <li>• Sales Order Management</li>
                          <li>• Delivery and Shipping</li>
                          <li>• Billing and Invoice Processing</li>
                          <li>• Credit Management and Collections</li>
                        </ul>
                      </div>
                    </section>

                    <section>
                      <h3 className="text-lg font-semibold mb-3 text-gray-800">
                        3. Key Requirements
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        The implementation must support multi-currency transactions, automated credit checks, integration with external logistics providers, and real-time inventory availability checks. The solution should maintain audit trails for all transactions and support role-based access controls.
                      </p>
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comments Panel */}
          {showComments && (
            <div className="w-80 border-l border-border bg-muted/20 flex flex-col">
              <div className="p-4 border-b border-border">
                <h3 className="text-lg font-semibold text-foreground">Comments</h3>
              </div>
              <div className="flex-1 overflow-auto p-4 space-y-4">
                {document?.comments?.map((comment, index) => (
                  <div key={index} className="bg-card p-3 rounded-lg border border-border">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-xs font-medium">
                        {comment?.author?.split(' ')?.map(n => n?.[0])?.join('')}
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {comment?.author}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Page {comment?.page}
                      </span>
                    </div>
                    <p className="text-sm text-foreground mb-2">
                      {comment?.text}
                    </p>
                    <span className="text-xs text-muted-foreground">
                      {new Date(comment.timestamp)?.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-border">
                <div className="space-y-3">
                  <textarea
                    value={newComment}
                    onChange={(e) => setNewComment(e?.target?.value)}
                    placeholder="Add a comment..."
                    className="w-full p-3 border border-border rounded-lg bg-background text-foreground text-sm resize-none"
                    rows={3}
                  />
                  <Button
                    variant="default"
                    size="sm"
                    fullWidth
                    onClick={handleAddComment}
                    disabled={!newComment?.trim()}
                  >
                    Add Comment
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-4 border-t border-border bg-muted/30">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-success" />
              <span className="text-sm text-muted-foreground">
                Document verified and encrypted
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
            {document?.canSign && (
              <Button
                variant="default"
                iconName="PenTool"
                iconPosition="left"
                onClick={() => onSign(document)}
              >
                Sign Document
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;